import type { NavItem } from './nav';

/**
 * Fallback path-segment → label-key map, used when the current route's nav
 * item is hidden for the effective role (e.g. a direct URL to a module the
 * sidebar doesn't show) so the breadcrumb still resolves a sensible label.
 */
const PATH_LABELS: Record<string, string> = {
  dashboard: 'nav.dashboard',
  lines: 'nav.lines',
  users: 'nav.users',
  requests: 'nav.requests',
  reports: 'nav.reports',
  invoices: 'nav.invoices',
  settings: 'nav.settings',
  team: 'nav.myTeam',
  'my-lines': 'nav.myLines',
};

const SETTINGS_LABELS: Record<string, string> = {
  catalog: 'settings.catalog',
  budgets: 'settings.budgets',
  notifications: 'settings.notifications',
  data: 'settings.data',
};

/**
 * Resolves the breadcrumb trail (a list of i18n keys, most-significant last)
 * for the current pathname. The `TopBar` prepends the customer name. When the
 * active nav item is visible it prefers that item's label (so a User on
 * `/dashboard` reads "My usage", matching the sidebar); otherwise it falls back
 * to the static map. Settings adds its sub-section as a second crumb.
 */
export function resolveTrail(items: NavItem[], pathname: string): string[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return ['nav.dashboard'];
  }

  const first = segments[0] ?? '';
  const second = segments[1];
  const firstPath = `/${first}`;
  const navItem = items.find((item) => item.to === firstPath);
  const topKey = navItem?.labelKey ?? PATH_LABELS[first] ?? 'app.name';
  const trail = [topKey];

  if (first === 'settings' && second && SETTINGS_LABELS[second]) {
    trail.push(SETTINGS_LABELS[second]);
  }

  return trail;
}
